import { yupResolver } from '@hookform/resolvers/yup';
import { Spinner, Text } from '@village/ui';
import { Banner } from 'components/banner';
import { millisecondsToMinutes } from 'date-fns';
import { PatientInformationFormInputs } from 'epion-checkin/components/form-inputs';
import { useCountly } from 'epion-checkin/hooks/use-countly';
import { useEpionCheckIn } from 'epion-checkin/hooks/use-epion-checkin-url';
import { EpionDemographicDataSchemaValidated, epionDemographicDataSchema } from 'epion-checkin/schema';
import { keys } from 'ramda';
import { FC, PropsWithChildren, useEffect, useRef } from 'react';
import { useForm, FormProvider, FieldErrors } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { EpionCheckinAppOptions } from 'types';

import * as Styled from './styles';

const resolver = yupResolver(epionDemographicDataSchema);

interface Props {
    options: EpionCheckinAppOptions;
    onTermsOfUseModalOpen: () => void;
}

export const EpionCheckInForm: FC<PropsWithChildren<Props>> = ({ options, onTermsOfUseModalOpen }) => {
    const { t, i18n } = useTranslation();
    const { addCountlyEvent } = useCountly();
    const durationStartRef = useRef<number>(Date.now());
    const errorBannerRef = useRef<PropsWithChildren<HTMLDivElement>>(null);
    const { mutate: getEpionCheckInUrl, error, status, data: epionCheckInUrl } = useEpionCheckIn();
    const isProcessingRedirect = status === 'pending' || status === 'success';

    const form = useForm<EpionDemographicDataSchemaValidated>({
        resolver,
        mode: 'onBlur',
        shouldFocusError: true,
        defaultValues: {
            dateOfBirth: undefined,
            address: undefined,
            addressCount: undefined,
            city: undefined,
            email: undefined,
            firstName: undefined,
            lastName: undefined,
            phoneNumber: undefined,
            phoneType: undefined,
            sex: undefined,
            state: undefined,
            zip: undefined,
            noEmail: false,
        },
    });

    const onSubmit = (patientData: EpionDemographicDataSchemaValidated) => {
        getEpionCheckInUrl({ patientData, departmentId: options.departmentId });
        addCountlyEvent({
            key: 'viewpatientintake.clickpatientintakecontinue',
            segmentation: {
                duration: millisecondsToMinutes(Date.now() - durationStartRef.current) || 1,
            },
        });
    };

    const onError = (errors: FieldErrors) => {
        console.log(errors, options);
    };

    useEffect(() => {
        window.onbeforeunload = keys(form.formState.dirtyFields).length > 0 ? () => true : null;

        return () => {
            window.onbeforeunload = null;
        };
    }, [form.formState.dirtyFields]);

    useEffect(() => {
        if (error) {
            errorBannerRef.current?.focus();
        }
    }, [error]);

    useEffect(() => {
        if (epionCheckInUrl) {
            window.onbeforeunload = null;

            const url = new URL(epionCheckInUrl);
            url.searchParams.set('locale', i18n.language);

            window.location.href = url.toString();
        }
    }, [epionCheckInUrl, i18n.language]);

    useEffect(() => {
        addCountlyEvent({ key: 'viewwelcome.viewpatientintake' });
    }, [addCountlyEvent]);

    return (
        <FormProvider {...form}>
            <Styled.Form onSubmit={form.handleSubmit(onSubmit, onError)}>
                <button type="submit" disabled hidden aria-description="Prevent implicit submission of the form" />
                <Styled.Title type="h4">{t('form.title')}</Styled.Title>

                {error ? (
                    <Banner ref={errorBannerRef} variant="default" status="critical" role="alert" tabIndex={0} iconSize={1.5}>
                        <Text type="body1">{t('form.submitFailed')}</Text>
                    </Banner>
                ) : null}

                <PatientInformationFormInputs />

                <Styled.TermsAndConditions type="body1">
                    <Trans
                        i18nKey="form.termsOfUse.title"
                        components={{ TermsLink: <Styled.Link onClick={onTermsOfUseModalOpen} /> }}
                    />
                </Styled.TermsAndConditions>

                <Styled.SubmitButton size="medium" fullWidth={true} type="submit" disabled={isProcessingRedirect}>
                    {isProcessingRedirect ? <Spinner size="2rem" /> : t('form.submit')}
                </Styled.SubmitButton>
            </Styled.Form>
        </FormProvider>
    );
};
